import React, { useState, useEffect, useRef } from 'react';
import {Box, Card, Typography, Grid, Badge, useTheme, Tooltip,Dialog, DialogTitle, DialogContent, DialogActions, Button, List,ListItem, Stack, Snackbar, Alert as MuiAlert, CircularProgress} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChatIcon from '@mui/icons-material/Chat';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import useSelection from 'hooks/useSelection';
import { useNavigate } from 'react-router';
import { formatDateWithTimezoneDisplay, isValidDateValue, formatDynamicScalar } from 'utils/dateFormat';
import KanbanMinimap from 'components/Minimapkanbanview';
import useApi from 'hooks/useApi';
import { useHasPermission } from 'contexts/PermissionContext';
import { PERMISSIONS } from 'contexts/PermissionContext';
import WarningModal from 'utils/warningModel/warningMdel';
import EditTransactionModal from './EditTransactionEvent';
import WarningIcon from '@mui/icons-material/Warning';

// ─── Types ────────────────────────────────────────────────────────────────────


interface Item {
  id: string;
  ticket: string;
  location: string;
  date: string;
  time: string;
}

interface Column {
  count: number;              // total transactions in this status (server-side, pagination-independent)
  name: string;
  items: Item[];
  statusKey?: string | null;  // keyStatus id ('none' for the No Status bucket) — identifies the column for load-more
  loadedParents?: number;     // top-level rows fetched so far (children excluded); drives the Load More button
  page?: number;              // last fetched page for this column
}

interface Columns {
  [key: string]: Column;
}

interface KanbanTransactionsProps {
  loading: boolean;
  transactionData: any;
  dateRange: any;
  search?: string;
  reportingStatus?: any;
  status?: string;
  team?: string;
  reportingStatusMap: any;
  onRefresh?: () => void;
  onEditModalStateChange?: (isOpen: boolean) => void;
  showPriorityOnly?: boolean;
  kanbanViewMode: 'compact' | 'full';
  isMaximized: boolean;
  onMaximizeChange: (v: boolean) => void;
  // Fetches the next page for one column; resolves to { keyStatus, count, rows } or null.
  onLoadMoreColumn?: (statusKey: string, page: number) => Promise<any | null>;
}

// ─── View config ──────────────────────────────────────────────────────────────

const VIEW_CONFIG = {
  compact: {
    cardWidth: 192,
    cardHeight: 230,
    columnHeaderFontSize: '0.62rem',
    titleFontSize: '0.6rem',
    labelFontSize: '0.5rem',
    valueFontSize: '10.5px',
    iconSize: 12,
    badgeFontSize: '0.38rem',
    badgeHeight: 10,
    cardPx: 0.75,
    cardPy: 0.6,
    gridSpacing: 0.5,
    mb: 0.6,
    countBadgeFontSize: '0.68rem',
    badgeIconSize: '0.85rem',
    viewHeight: '90vh',
  },
  full: {
    cardWidth: 268,
    cardHeight: 200,
    columnHeaderFontSize: '0.75rem',
    titleFontSize: '0.76rem',
    labelFontSize: '0.6rem',
    valueFontSize: '12.5px',
    iconSize: 15,
    badgeFontSize: '0.52rem',
    badgeHeight: 13,
    cardPx: 1.25,
    cardPy: 0.75,
    gridSpacing: 1,
    mb: 0.85,
    countBadgeFontSize: '0.78rem',
    badgeIconSize: '1rem',
    viewHeight: '96vh',
  },
};

// ─── Palette ──────────────────────────────────────────────────────────────────

const PALETTE = {
  columnBg: '#F3F4F6',
  cardBg: '#FFFFFF',
  headerBg: 'rgba(255,255,255,0.92)',
  border: '#E5E7EB',
  borderAccent: '#D1D5DB',
  shadow: '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)',
  shadowHover: '0 4px 12px rgba(0,0,0,0.10)',
  success: '#10B981',
  successHover: '#059669',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getFileIcon = (mimeType: string) => {
  if (mimeType?.includes('pdf')) return '📄';
  if (mimeType?.includes('image')) return '🖼️';
  if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet') || mimeType?.includes('xls')) return '📊';
  if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
  if (mimeType?.includes('video')) return '🎥';
  return '📎';
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const ColumnHeader: React.FC<{
  name: string;
  count: number;
  priorityCount: number;  // ← add
  cfg: typeof VIEW_CONFIG['compact'];
  accentColor: string;
  isMaximized: boolean;
}> = ({ name, count, priorityCount, cfg, accentColor, isMaximized }) => (
  <Box
    sx={{
      position: 'sticky',
      top: isMaximized ? '0px' : 0,
      zIndex: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: cfg.cardWidth,
      minWidth: cfg.cardWidth,
      px: 1,
      py: '6px',
      backdropFilter: 'blur(8px)',
      backgroundColor: PALETTE.headerBg,
      borderBottom: `1px solid ${PALETTE.border}`,
      borderTop: `2px solid ${accentColor}`,
      transition: 'width 0.2s ease',
    }}
  >
    <Tooltip title={name} arrow placement="top">
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: cfg.columnHeaderFontSize,
          color: '#374151',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          flex: 1,
        }}
      >
        {name}
      </Typography>
    </Tooltip>

    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', ml: 0.5 }}>
      
      {/* Total count badge */}
      <Box
        sx={{
          minWidth: 20,
          height: 20,
          px: 0.6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '6px',
          backgroundColor: accentColor,
          color: '#fff',
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: cfg.countBadgeFontSize, lineHeight: 1 }}>
          {count}
        </Typography>
      </Box>
    </Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const KanbanTransactions: React.FC<KanbanTransactionsProps> = ({
  loading,
  transactionData,
  dateRange,
  search,
  reportingStatus,
  status,
  team,
  reportingStatusMap,
  onRefresh,
  onEditModalStateChange,
  showPriorityOnly = false,
  kanbanViewMode,        // ← new
  isMaximized,          // ← new
  onMaximizeChange,
  onLoadMoreColumn,
}) => {
  const {  selectedClient } = useSelection();
  const [data, setData] = useState<any>();
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  // ── View mode ──
  const cfg = VIEW_CONFIG[kanbanViewMode];


  // ── Delete state ──
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [transactionToEdit, setTransactionToEdit] = useState<any>(null);
  const [editStepData, setEditStepsData] = useState<any>(null);

  // ── File hover state ──
  const [hoveredFiles, setHoveredFiles] = useState<any[]>([]);
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingTx, setPendingTx] = useState<any>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [acknowledgedTxIds, setAcknowledgedTxIds] = useState<Set<string>>(new Set());

  const { deleteTransaction, showToast, editTransactionDetails, updateNotificationStatus } = useApi();
  const hasDeletePermission = useHasPermission(PERMISSIONS.DELETE_TRANSACTIONS);
  const [expandedNotifications, setExpandedNotifications] = useState<Set<string>>(new Set());

  const accentColor = theme.palette.secondary.dark;

  const toggleNotificationAccordion = (e: React.MouseEvent, cardId: string) => {
    e.stopPropagation();
    setExpandedNotifications(prev => {
      const next = new Set(prev);
      next.has(cardId) ? next.delete(cardId) : next.add(cardId);
      return next;
    });
  };

  const handleEdit = async (e: React.MouseEvent, item: any) => {
    e.stopPropagation();
    setTransactionToEdit(item);
    setEditModalOpen(true);
    try {
      const response = await editTransactionDetails(item._id);
      if (response?.data?.data.completedSteps) {
        setEditStepsData(response.data.data.completedSteps);
      } else {
        setEditStepsData([]); // Set to empty array if no steps
      }
    } catch (error) {
      showToast('Failed to load transaction details', 'error');
      setEditStepsData([]);
    }
  };

  const groupByDisplayKeyStatus = (items: any[] = [], allStatuses: any[] = []) => {
    const groups: { [key: string]: any[] } = {};
    const statusList = allStatuses || [];

  // Initialize all statuses with empty arrays
    statusList.forEach(status => {
      const statusKey = typeof status === 'string'
        ? status
        : (status?.displayName || status?.name || status?.label || 'Unknown');
      groups[statusKey] = [];
    });
    if (Array.isArray(items) && items.length > 0) {
      const safeItems = items.filter(item => item != null);
      safeItems.forEach((item: any) => {
        const key = (reportingStatusMap && reportingStatusMap[item?.keyStatus]) || 'No Status';
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });
    }

    const sorted = Object.entries(groups).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );

    setData({
      columns: sorted.reduce((acc, [k, v]) => {
        acc[k] = { count: v.length, name: k, items: v };
        return acc;
      }, {} as Columns),
    });
  };

  const formatResourceValue = (value: any): string => {
    if (Array.isArray(value) && value.length > 0) {
      const first = value[0];
      if (typeof first === 'object' && first !== null && first.fields && Object.keys(first.fields).length > 0) {
        const result = value.slice(0, 2)
          .map((r: any) => Object.values(r.fields).slice(0, 2).map(v => String(v)).join(', '))
          .join(' | ');
        return value.length > 2 ? result + ' …' : result;
      }
    }
    return String(value);
  };

  const formatToLocalTime = (value: any) => formatDateWithTimezoneDisplay(value);

 useEffect(() => {
  const handleEsc = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isMaximized) onMaximizeChange(false);
  };
  if (isMaximized) {
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }
}, [isMaximized]);

  // Re-group whenever transactions, team/client/date, OR the reporting-status
  // map changes. The map can land AFTER transactions if the team-switch fetches
  // race — without it in deps, items stay bucketed under "No Status" until the
  // next unrelated re-render.
  useEffect(() => { getTransactions(); }, [team, selectedClient, dateRange, transactionData, reportingStatus, reportingStatusMap]);

  const flattenRows = (rows: any[]) => {
    const flat: any[] = [];
    (rows || []).forEach((parent: any) => {
      flat.push(parent);
      (parent.children || []).forEach((child: any) => flat.push({ ...child, parentKey: parent.key }));
    });
    return flat;
  };

  const collectExpandedNotifications = (items: any[], base?: Set<string>) => {
    const expanded = new Set<string>(base);
    items.forEach((item: any) => {
      const cardId = item._id || item.id;
      if (Number(item?.notificationCount) > 0 && !acknowledgedTxIds.has(item.uid || item._id)) {
        expanded.add(cardId);
      }
    });
    return expanded;
  };

  const statusNameFor = (keyStatus: any) =>
    (reportingStatusMap && reportingStatusMap[keyStatus]) || 'No Status';

  // Server-driven kanban shape: { counts: [{keyStatus, count}], columns:
  // [{keyStatus, count, rows}] }. Column counts come from the server
  // aggregation and are independent of how many cards are loaded; cards are
  // still bucketed by their own keyStatus (children can land in a different
  // column than the parent they were fetched with, same as before).
  const buildColumnsFromServer = () => {
    const groups: { [key: string]: Column } = {};
    (reportingStatus || []).forEach((st: any) => {
      const statusName = typeof st === 'string'
        ? st
        : (st?.displayName || st?.name || st?.label || 'Unknown');
      groups[statusName] = {
        count: 0,
        name: statusName,
        items: [],
        statusKey: typeof st === 'object' ? st?._id?.toString() : undefined,
        loadedParents: 0,
        page: 1,
      };
    });

    (transactionData.counts || []).forEach((c: any) => {
      const name = statusNameFor(c.keyStatus);
      if (!groups[name]) {
        groups[name] = { count: 0, name, items: [], statusKey: c.keyStatus ?? 'none', loadedParents: 0, page: 1 };
      }
      groups[name].count += c.count;
      if (groups[name].statusKey === undefined) groups[name].statusKey = c.keyStatus ?? 'none';
    });

    const allFlat: any[] = [];
    (transactionData.columns || []).forEach((col: any) => {
      const serverName = statusNameFor(col.keyStatus);
      if (groups[serverName]) {
        groups[serverName].loadedParents = (groups[serverName].loadedParents || 0) + (col.rows?.length || 0);
      }
      flattenRows(col.rows).forEach((item: any) => {
        const name = statusNameFor(item?.keyStatus);
        if (!groups[name]) {
          groups[name] = { count: 0, name, items: [], statusKey: item?.keyStatus ?? 'none', loadedParents: 0, page: 1 };
        }
        groups[name].items.push(item);
        allFlat.push(item);
      });
    });

    const sorted = Object.entries(groups).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
    setData({
      columns: sorted.reduce((acc, [k, v]) => {
        acc[k] = v;
        return acc;
      }, {} as Columns),
    });
    setExpandedNotifications(collectExpandedNotifications(allFlat));
  };

  const getTransactions = async () => {
    if (loading) return;
    if (transactionData?.columns) {
      buildColumnsFromServer();
      return;
    }
    // Legacy flat-rows shape (deploy skew / older endpoint) — group client-side.
    if (transactionData?.rows) {
      const flat = flattenRows(transactionData.rows);
      groupByDisplayKeyStatus(flat, reportingStatus);
      setExpandedNotifications(collectExpandedNotifications(flat));
    }
  };

  // ── Per-column "Load more" ──
  const [loadingMoreColumns, setLoadingMoreColumns] = useState<Set<string>>(new Set());

  const handleLoadMore = async (columnName: string) => {
    const col: Column | undefined = data?.columns?.[columnName];
    if (!col || !onLoadMoreColumn || col.statusKey === undefined || loadingMoreColumns.has(columnName)) return;
    const nextPage = (col.page || 1) + 1;
    setLoadingMoreColumns(prev => new Set(prev).add(columnName));
    try {
      const result = await onLoadMoreColumn(col.statusKey ?? 'none', nextPage);
      if (result?.rows?.length) {
        const flat = flattenRows(result.rows);
        setExpandedNotifications(prev => collectExpandedNotifications(flat, prev));
        setData((prev: any) => {
          if (!prev?.columns) return prev;
          const updated: Columns = { ...prev.columns };
          const target = updated[columnName];
          if (target) {
            updated[columnName] = {
              ...target,
              page: nextPage,
              loadedParents: (target.loadedParents || 0) + result.rows.length,
              count: typeof result.count === 'number' ? result.count : target.count,
            };
          }
          flat.forEach((item: any) => {
            const name = statusNameFor(item?.keyStatus);
            const colRef = updated[name] || { count: 0, name, items: [], statusKey: item?.keyStatus ?? 'none', loadedParents: 0, page: 1 };
            const id = (item._id || item.id)?.toString();
            // Guard against page-boundary duplicates when new transactions
            // arrived between the initial load and this fetch.
            if (colRef.items.some((existing: any) => ((existing._id || existing.id)?.toString()) === id)) return;
            updated[name] = { ...colRef, items: [...colRef.items, item] };
          });
          return { columns: updated };
        });
      } else if (result && Array.isArray(result.rows)) {
        // Empty page — server has fewer rows than the count suggested (e.g.
        // deletions since load). Sync loadedParents to count to hide the button.
        setData((prev: any) => {
          if (!prev?.columns?.[columnName]) return prev;
          const updated: Columns = { ...prev.columns };
          updated[columnName] = { ...updated[columnName], loadedParents: updated[columnName].count, page: nextPage };
          return { columns: updated };
        });
      }
    } catch (error) {
      showToast('Failed to load more transactions', 'error');
    } finally {
      setLoadingMoreColumns(prev => {
        const next = new Set(prev);
        next.delete(columnName);
        return next;
      });
    }
  };

  // Keeps the observer callback (set up once below) calling the current
  // handleLoadMore closure instead of a stale one from whenever the
  // IntersectionObserver was created.
  const handleLoadMoreRef = useRef(handleLoadMore);
  handleLoadMoreRef.current = handleLoadMore;

  // Auto-trigger "Load more" when its sentinel scrolls into view within the
  // shared board scroll container — columns aren't independently
  // scrollable (the whole board scrolls as one), so each column's sentinel
  // is observed individually rather than listening for a per-column scroll
  // event. The button stays as a manual fallback.
  const columnObserverRef = useRef<IntersectionObserver | null>(null);
  const sentinelElsRef = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const columnId = entry.target.getAttribute('data-column-id');
          if (columnId) void handleLoadMoreRef.current(columnId);
        });
      },
      { root, rootMargin: '200px', threshold: 0 },
    );
    columnObserverRef.current = observer;
    sentinelElsRef.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setSentinelRef = (columnId: string) => (el: HTMLDivElement | null) => {
    const observer = columnObserverRef.current;
    const prevEl = sentinelElsRef.current.get(columnId);
    if (prevEl && observer) observer.unobserve(prevEl);
    if (el) {
      sentinelElsRef.current.set(columnId, el);
      observer?.observe(el);
    } else {
      sentinelElsRef.current.delete(columnId);
    }
  };

  const handleDelete = (e: React.MouseEvent, item: any) => {
    e.stopPropagation();
    setTransactionToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!transactionToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteTransaction(transactionToDelete.key);
      showToast('Transaction deleted successfully', 'success');
      if (data?.columns) {
        const updated = { ...data.columns };
        Object.keys(updated).forEach(k => {
          const remaining = updated[k].items.filter((i: any) => i.key !== transactionToDelete.key);
          const removed = updated[k].items.length - remaining.length;
          updated[k] = {
            ...updated[k],
            items: remaining,
            // Decrement the total instead of recomputing from loaded items —
            // server-driven counts cover unloaded cards too.
            count: Math.max(0, updated[k].count - removed),
            loadedParents: Math.max(0, (updated[k].loadedParents ?? remaining.length) - removed),
          };
        });
        setData({ columns: updated });
      }
      onRefresh?.();
      setDeleteModalOpen(false);
      setTransactionToDelete(null);
    } catch {
      showToast('Failed to delete transaction', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAcknowledge = async (tx: any) => {
    if (tx?.notificationuid) {
      try {
        await updateNotificationStatus(tx.notificationuid, false);
        setAcknowledgedTxIds(prev => new Set(prev).add(tx.uid || tx._id));
        setSnackbarMsg('Transaction acknowledged successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch {
        setSnackbarMsg('Failed to acknowledge transaction');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  useEffect(() => {
    onEditModalStateChange?.(editModalOpen);
    return () => {
      onEditModalStateChange?.(false);
    };
  }, [editModalOpen, onEditModalStateChange]);

  // ─── Derived: filtered columns for priority-only mode ───────────────────────

  const displayColumns = React.useMemo(() => {
    if (!data?.columns || !showPriorityOnly) return data?.columns;
    const filtered: Columns = {};
    Object.entries(data.columns).forEach(([key, col]) => {
      const c = col as Column;
      const priorityItems = c.items.filter((item: any) =>
        Number(item?.notificationCount) > 0 && !acknowledgedTxIds.has(item.uid || item._id)
      );
      // Server-driven columns carry a pagination-independent total (the
      // kanban endpoint already filtered by priority), so only subtract the
      // items hidden client-side; legacy columns (count === items.length)
      // reduce to priorityItems.length exactly as before.
      const removed = c.items.length - priorityItems.length;
      filtered[key] = { ...c, items: priorityItems, count: Math.max(priorityItems.length, c.count - removed) };
    });
    return filtered;
  }, [data?.columns, showPriorityOnly, acknowledgedTxIds]);

  

  

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert elevation={6} variant="filled" onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: '10px', fontSize: '0.85rem' }}>
          {snackbarMsg}
        </MuiAlert>
      </Snackbar>

      {/* Fullscreen backdrop */}
      {isMaximized && (
        <Box sx={{ position: 'fixed', inset: 0, backgroundColor: '#F9FAFB', zIndex: 1299 }} />
      )}

      {/* Board container */}
      <Box
        sx={{
          ...(isMaximized && { position: 'fixed', inset: 0, zIndex: 1300,paddingTop: '45px' }),
          width: '100%',
          backgroundColor: '#F9FAFB',
          borderRadius: isMaximized ? 0 : '10px',
          border: isMaximized ? 'none' : `1px solid ${PALETTE.border}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* ── Single board-level scroll — handles both X and Y ── */}
        <Box
          ref={scrollContainerRef}
          sx={{
            maxHeight: isMaximized ? 'calc(100vh - 44px)' : cfg.viewHeight,
            overflowX: 'auto',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { height: '6px', width: '5px' },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#D1D5DB', borderRadius: '8px' },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9CA3AF' },
            '&::-webkit-scrollbar-button': { display: 'none' },
          }}
        >
          {/* ── Empty states ── */}
          {(!selectedClient || !team || team === 'all') ? (
            <Box sx={{ py: 10, textAlign: 'center', px: 3 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#374151', mb: 0.5 }}>
                {!selectedClient ? 'No client selected' : 'Select a specific process'}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#9CA3AF' }}>
                {!selectedClient
                  ? 'Please select a client and process to view transactions.'
                  : "Please select a particular process instead of 'All' to view transactions."}
              </Typography>
            </Box>
          ) : loading ? (
            <Box sx={{ py: 10, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={28} sx={{ color: accentColor }} />
              <Typography sx={{ fontSize: '0.88rem', color: '#9CA3AF' }}>Loading transactions…</Typography>
            </Box>
          ) : displayColumns ? (
            Object.keys(displayColumns).length > 0 ? (
              /* Columns row — expands horizontally, never wraps */
              <Grid container spacing={0} wrap="nowrap" sx={{ width: 'max-content', p: 1.5, gap: '10px', pt:'28px' }}>
                {(() => {
                  const entries = Object.entries(displayColumns);
                  const firstIdx = entries.findIndex(([k]) => k === 'First');
                  const ordered = firstIdx !== -1
                    ? [entries[firstIdx], ...entries.slice(0, firstIdx), ...entries.slice(firstIdx + 1)]
                    : entries;

                  return ordered.map(([columnId, column]) => {
                    const col = column as Column;
                    const colPriorityCount = col.items.filter((item: any) =>
                          item?.priorityType === 'ALERT' &&
                          Number(item?.notificationCount) > 0 &&
                          !acknowledgedTxIds.has(item.uid || item._id)
                        ).length;
                    return (
                      <Grid
                        item
                        key={columnId}
                        sx={{
                          backgroundColor: PALETTE.columnBg,
                          borderRadius: '10px',
                          border: `1px solid ${PALETTE.border}`,
                          paddingLeft: '0 !important',
                          position: 'relative', 
                        }}
                      >
                        {colPriorityCount > 0 && (
                          <Tooltip title={`${colPriorityCount} alert${colPriorityCount > 1 ? 's' : ''}`} arrow placement="top">
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -27,
                                right: 8,
                                height: 22,
                                px: 0.75,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                borderRadius: '6px',
                                backgroundColor: '#FEF3C7',
                                border: '1px solid #FCD34D',
                                zIndex: 20,
                                boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                              }}
                            >
                              <WarningIcon sx={{ fontSize: '0.65rem', color: '#92400E' }} />
                              <Typography sx={{ fontWeight: 700, fontSize: cfg.countBadgeFontSize, lineHeight: 1, color: '#92400E' }}>
                                {colPriorityCount}
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}

                        {/* Column header — sticky within the shared scroll container */}
                        <ColumnHeader name={col.name} count={col.count} priorityCount={colPriorityCount} cfg={cfg} accentColor={accentColor} isMaximized={isMaximized} />

                        {/* Cards — grow freely, no per-column scrollbar */}
                        <Box sx={{ minHeight: 200, p: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {col.items.length === 0 && showPriorityOnly ? (
                            <Box sx={{ width: cfg.cardWidth, py: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography sx={{ fontSize: '0.72rem', color: '#D1D5DB' }}>No priority items</Typography>
                            </Box>
                          ) : (
                            col.items.map((item: any) => {
                              const cardId = item._id || item.id;
                              const isAlert = item?.priorityType === 'ALERT';
                              const hasNotif = Number(item?.notificationCount) > 0 && !acknowledgedTxIds.has(item.uid || item._id);
                              const notifExpanded = expandedNotifications.has(cardId);

                              return (
                                <Card
                                  key={cardId}
                                  onClick={() => navigate(`/transactions/details/${item?.team?.uid}/${item?.uid}/${item?.key}`)}
                                  sx={{
                                    width: cfg.cardWidth,
                                    maxHeight: cfg.cardHeight,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    cursor: 'pointer',
                                    borderRadius: '8px',
                                    backgroundColor: PALETTE.cardBg,
                                    border: `1px solid ${PALETTE.border}`,
                                    borderLeft: `3px solid ${hasNotif ? (isAlert ? theme.palette.accent.dark : theme.palette.accent.main) : "#eee"}`,
                                    boxShadow: PALETTE.shadow,
                                    overflow: 'visible',
                                    transition: 'box-shadow 0.18s ease, transform 0.18s ease',
                                    '&:hover': {
                                      boxShadow: PALETTE.shadowHover,
                                      transform: 'translateY(-1px)',
                                    },
                                  }}
                                >
                                  {/* Card Header */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      justifyContent: 'space-between',
                                      gap: 0.5,
                                      px: cfg.cardPx,
                                      pt: cfg.cardPy,
                                      pb: '5px',
                                      borderBottom: `1px solid ${PALETTE.border}`,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {/* Title area */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography
                                        sx={{
                                          fontSize: cfg.titleFontSize,
                                          fontWeight: 700,
                                          color: '#111827',
                                          lineHeight: 1.25,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.03em',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          marginTop: '6px'
                                        }}
                                      >
                                        {item?.additionalFields?.['shipment number'] ||
                                          item?.additionalFields?.['Shipment Number'] ||
                                          item?.additionalFields?.['shipment_number'] ||
                                          item?.key || '—'}
                                      </Typography>
                                      {Number((item as any)?.alertCount) > 0 && (
                                        <Box
                                          title={`${(item as any).alertCount} alert${(item as any).alertCount === 1 ? '' : 's'}`}
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minWidth: 16,
                                            height: 16,
                                            px: 0.5,
                                            fontSize: '0.6rem',
                                            fontWeight: 700,
                                            color: '#fff',
                                            bgcolor: 'error.main',
                                            borderRadius: '8px',
                                            lineHeight: 1,
                                            marginTop: '6px',
                                          }}
                                        >
                                          {(item as any).alertCount > 99 ? '99+' : (item as any).alertCount}
                                        </Box>
                                      )}
                                      {(() => {
                                        const s = ((item as any)?.driverState || '').toUpperCase();
                                        const meta = s === 'RESTING'
                                          ? { label: 'Resting', color: '#ff9800' }
                                          : s === 'STOPPED'
                                          ? { label: 'Stopped', color: '#d32f2f' }
                                          : s === 'SWAPPED'
                                          ? { label: 'Swapped', color: '#757575' }
                                          : null;
                                        if (!meta) return null;
                                        return (
                                          <Box
                                            title={`Driver ${meta.label}`}
                                            sx={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              height: 16,
                                              px: 0.6,
                                              fontSize: '0.55rem',
                                              fontWeight: 700,
                                              color: '#fff',
                                              bgcolor: meta.color,
                                              borderRadius: '8px',
                                              lineHeight: 1,
                                              marginTop: '6px',
                                              textTransform: 'uppercase',
                                              letterSpacing: 0.3,
                                            }}
                                          >
                                            {meta.label}
                                          </Box>
                                        );
                                      })()}
                                      </Box>
                                      {item?.parentKey && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: '2px' }}>
                                          <Typography sx={{ fontSize: cfg.labelFontSize, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            Parent:
                                          </Typography>
                                          <Typography sx={{ fontSize: cfg.labelFontSize, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>
                                            {item.parentKey}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>

                                    {/* Action icons */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0, ml: 0.25 }}>
                                        {(Array.isArray(item?.file_Attachment) || item?.file_Attachment) && (
                                          <Box
                                            onMouseEnter={() => { setHoveredFiles(item?.file_attachmet_list || []); setHoveredCardId(cardId); }}
                                            onMouseLeave={() => {
                                              // small delay so moving mouse toward popup doesn't close it
                                              setTimeout(() => {
                                                setHoveredFiles([]);
                                                setHoveredCardId(null);
                                              }, 120);
                                            }}
                                            sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                                          >
                                            <Box sx={{
                                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                                              width: 18, height: 18, borderRadius: '4px',
                                              backgroundColor: `${theme.palette.primary.main}18`, cursor: 'pointer',
                                            }}>
                                              <AttachFileIcon sx={{ fontSize: cfg.iconSize - 1, color: theme.palette.primary.main }} />
                                            </Box>

                                            {/* File hover popup */}
                                            {hoveredCardId === cardId && hoveredFiles.length > 0 && (
                                              <Box
                                                onMouseEnter={() => {
                                                  // keep open while hovering popup itself
                                                  setHoveredCardId(cardId);
                                                }}
                                                onMouseLeave={() => {
                                                  setHoveredFiles([]);
                                                  setHoveredCardId(null);
                                                }}
                                                sx={{
                                                  position: 'absolute',
                                                  top: '100%',
                                                  right: 0,           // ← anchor to right edge of icon, not centered
                                                  mt: '4px',
                                                  bgcolor: '#fff',
                                                  borderRadius: '10px',
                                                  boxShadow: '0 8px 30px rgba(0,0,0,0.14)',
                                                  p: '10px',
                                                  minWidth: 220,
                                                  maxWidth: 300,
                                                  zIndex: 999999,
                                                  pointerEvents: 'auto',   // ← allow hover on popup itself
                                                  border: `1px solid ${PALETTE.border}`,
                                                }}
                                              >
                                                <Typography sx={{
                                                  fontSize: '0.65rem', fontWeight: 700, color: '#9CA3AF',
                                                  textTransform: 'uppercase', letterSpacing: '0.05em', mb: '6px', pl: '2px',
                                                }}>
                                                  Attachments ({hoveredFiles.length})
                                                </Typography>

                                                {hoveredFiles.map((file: any, i: number) => (
                                                  <Box
                                                    key={file._id || i}
                                                    sx={{
                                                      display: 'flex', alignItems: 'center', gap: '8px',
                                                      p: '6px 8px', borderRadius: '6px',
                                                      backgroundColor: '#F9FAFB', border: `1px solid ${PALETTE.border}`,
                                                      mb: i < hoveredFiles.length - 1 ? '4px' : 0,
                                                    }}
                                                  >
                                                    <Typography sx={{ fontSize: '0.9rem', lineHeight: 1 }}>
                                                      {getFileIcon(file.mime_type)}
                                                    </Typography>
                                                    <Tooltip title={file.file_name} placement="top">
                                                      <Typography sx={{
                                                        fontSize: '0.72rem', fontWeight: 600, color: '#374151',
                                                        whiteSpace: 'nowrap', overflow: 'hidden',
                                                        textOverflow: 'ellipsis', flex: 1,
                                                        maxWidth: 160,
                                                      }}>
                                                        {file.file_name}
                                                      </Typography>
                                                    </Tooltip>
                                                  </Box>
                                                ))}
                                              </Box>
                                            )}
                                          </Box>
                                        )}

                                      {/* Chat badge */}
                                      {item?.unreadChatCount > 0 && (
                                        <Badge
                                          badgeContent={item.unreadChatCount}
                                          color="error"
                                          max={99}
                                          sx={{ '& .MuiBadge-badge': { fontSize: cfg.badgeFontSize, height: cfg.badgeHeight, minWidth: cfg.badgeHeight } }}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '4px', backgroundColor: `${theme.palette.primary.main}18` }}>
                                            <ChatIcon sx={{ fontSize: cfg.iconSize - 1, color: theme.palette.primary.main }} />
                                          </Box>
                                        </Badge>
                                      )}

                                      {/* Delete */}
                                      <Tooltip title={hasDeletePermission ? 'Delete' : 'No permission'} placement="top">
                                        <Box
                                          onClick={(e) => hasDeletePermission && handleDelete(e, item)}
                                          sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            width: 18, height: 18, borderRadius: '4px',
                                            backgroundColor: hasDeletePermission ? `${theme.palette.error.main}15` : 'transparent',
                                            cursor: hasDeletePermission ? 'pointer' : 'default',
                                            opacity: hasDeletePermission ? 1 : 0.35,
                                            transition: 'background-color 0.15s',
                                            '&:hover': hasDeletePermission ? { backgroundColor: `${theme.palette.error.main}28` } : {},
                                          }}
                                        >
                                          <DeleteOutlineIcon sx={{ fontSize: cfg.iconSize - 1, color: hasDeletePermission ? theme.palette.error.main : '#9CA3AF' }} />
                                        </Box>
                                      </Tooltip>

                                    {/* Notification / Alert badge */}
                                    {hasNotif && (
                                      <Tooltip title={isAlert ? 'View alert reasons' : 'View notification reasons'} placement="top">
                                        <Box
                                          onClick={(e) => toggleNotificationAccordion(e, cardId)}
                                          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', position: 'relative' }}
                                        >
                                          <Badge
                                            badgeContent={item.notificationCount}
                                            max={99}
                                            sx={{
                                              '& .MuiBadge-badge': {
                                                fontSize: `calc(${cfg.badgeFontSize} + 0.06rem)`,
                                                height: cfg.badgeHeight + 2,
                                                minWidth: cfg.badgeHeight + 2,
                                                color: '#fff',
                                                fontWeight: 700,
                                                backgroundColor: isAlert ? theme.palette.accent.dark : theme.palette.accent.main,
                                                boxShadow: '0 0 0 1.5px #fff',
                                              },
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 22,
                                                height: 22,
                                                borderRadius: '5px',
                                                backgroundColor: isAlert ? `${theme.palette.accent.dark}20` : `${theme.palette.accent.main}20`,
                                                border: `1px solid ${isAlert ? `${theme.palette.accent.dark}40` : `${theme.palette.accent.main}40`}`,
                                                transition: 'background-color 0.15s, transform 0.15s',
                                                '&:hover': {
                                                  backgroundColor: isAlert ? `${theme.palette.accent.dark}35` : `${theme.palette.accent.main}35`,
                                                  transform: 'scale(1.08)',
                                                },
                                              }}
                                            >
                                              {isAlert
                                                ? <WarningIcon sx={{ fontSize: `calc(${cfg.badgeIconSize} + 0.18rem)`, color: theme.palette.accent.dark }} />
                                                : <NotificationsIcon sx={{ fontSize: `calc(${cfg.badgeIconSize} + 0.18rem)`, color: theme.palette.accent.main }} />
                                              }
                                            </Box>
                                          </Badge>
                                        </Box>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Box>
                                   {/* Card Body */}
                                <Box
                                  sx={{
                                    px: 0.75,
                                    py: '5px',
                                    flex: 1,
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    position: 'relative',
                                    '&::-webkit-scrollbar': { width: '2px' },
                                    '&::-webkit-scrollbar-thumb': { backgroundColor: '#E5E7EB', borderRadius: '2px' },
                                  }}
                                >
                                {/* Notification expanded panel */}
                                {hasNotif && Array.isArray(item?.notificationReasons) && item.notificationReasons.length > 0 && notifExpanded && (
                                  <Box
                                    onClick={(e) => toggleNotificationAccordion(e, cardId)}
                                    sx={{
                                      px: 1,
                                      pt: '6px',
                                      pb: '6px',
                                      borderBottom: `1px solid ${isAlert ? `${theme.palette.accent.dark}65` : `${theme.palette.accent.main}65`}`,
                                      backgroundColor: isAlert ? `${theme.palette.accent.dark}55` : `${theme.palette.accent.main}58`,
                                      cursor: 'pointer',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: "#000", mb: '3px' }}>
                                      {isAlert ?  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><WarningIcon sx={{ fontSize: "12px" }} /><span>Alert Reasons</span></Box> : '🔔 Notification Reasons'}
                                    </Typography>
                                    {item.notificationReasons.map((r: string, i: number) => (
                                      <Typography key={i} sx={{ fontSize: '0.6rem', color: '#374151', lineHeight: 1.5 }}>
                                        {i + 1}. {r}
                                      </Typography>
                                    ))}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: '4px' }}>
                                      <Tooltip title="Acknowledge">
                                        <Box
                                          onClick={(e) => { e.stopPropagation(); setPendingTx(item); setConfirmOpen(true); }}
                                          sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            width: 20, height: 20, borderRadius: '5px',
                                            backgroundColor: PALETTE.success,
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: PALETTE.successHover },
                                            transition: 'background-color 0.15s',
                                          }}
                                        >
                                          <CheckCircleOutlineIcon sx={{ fontSize: 11, color: '#fff' }} />
                                        </Box>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                )}

                             
                                {/* AI Detection Results — filter out the same
                                    log-only "couldn't evaluate" rows the details
                                    page filters. Otherwise unresolved sources
                                    inflate the card's count vs. the details
                                    AI Summary. */}
                                {(() => {
                                  if (!item?.hasDetection || !Array.isArray(item?.detectionSummary) || item.detectionSummary.length === 0) {
                                    return null;
                                  }
                                  const visible = item.detectionSummary.filter(
                                    (d: any) => d && d.sourcesResolved !== false,
                                  );
                                  if (visible.length === 0) return null;
                                  return (
                                  <Box
                                    sx={{
                                      px: 1,
                                      pt: '5px',
                                      pb: '5px',
                                      mb: '4px',
                                      borderBottom: '1px solid rgba(76, 175, 80, 0.3)',
                                      backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#2e7d32', mb: '3px' }}>
                                      AI Detection
                                    </Typography>
                                    {visible.slice(0, 3).map((det: any, i: number) => {
                                      const labelMap: any = { true: 'Match', false: 'No Match', other: 'Inconclusive' };
                                      const colorMap: any = { true: '#4caf50', false: '#f44336', other: '#ff9800' };
                                      return (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: '2px', minWidth: 0 }}>
                                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: colorMap[det.type] || '#999', flexShrink: 0 }} />
                                          <Typography noWrap sx={{ fontSize: '0.6rem', color: '#374151', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <strong>{labelMap[det.type] || det.type}</strong>
                                            {det.aiMessage ? ` — ${det.aiMessage}` : ''}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                    {visible.length > 3 && (
                                      <Typography sx={{ fontSize: '0.55rem', color: '#6B7280', mt: '2px' }}>
                                        +{visible.length - 3} more
                                      </Typography>
                                    )}
                                  </Box>
                                  );
                                })()}

                                  <Grid container spacing={cfg.gridSpacing}>
                                    {Object.entries(item?.additionalFields as Record<string, unknown> || {}).map(
                                      ([label, value]: [string, unknown], i: number) => (
                                        <Grid item xs={6} key={i} sx={{ pb: '2px' }}>
                                          <Typography sx={{ fontSize: cfg.labelFontSize, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.4 }}>
                                            {label}
                                          </Typography>
                                          <Typography sx={{ fontSize: cfg.valueFontSize, fontWeight: 600, color: '#111827', lineHeight: 1.25, wordBreak: 'break-word' }}>
                                            {value !== undefined && value !== null && value !== ''
                                              ? Array.isArray(value)
                                                ? value.length > 0 && typeof value[0] === 'object' && value[0] !== null && 'fields' in value[0]
                                                  ? formatResourceValue(value)
                                                  : value.map(v => formatDynamicScalar(v)).join(', ')
                                                : isValidDateValue(value)
                                                ? formatToLocalTime(value)
                                                : String(value)
                                              : <span style={{ color: '#D1D5DB' }}>—</span>}
                                          </Typography>
                                        </Grid>
                                      )
                                    )}

                                      {item?.team?.reportingStatus && item?.keyStatus && (
                                        <Grid item xs={6}>
                                          <Typography sx={{ fontSize: cfg.labelFontSize, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</Typography>
                                          <Typography sx={{ fontSize: cfg.valueFontSize, fontWeight: 600, color: '#111827', lineHeight: 1.25 }}>
                                            {reportingStatusMap?.[item?.keyStatus] || '—'}
                                          </Typography>
                                        </Grid>
                                      )}

                                      {item?.team?.displayStepStatus && (
                                        <Grid item xs={6}>
                                          <Typography sx={{ fontSize: cfg.labelFontSize, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.4 }}>
                                            Activity
                                          </Typography>
                                          <Typography sx={{ fontSize: cfg.valueFontSize, fontWeight: 600, color: '#111827', lineHeight: 1.25 }}>
                                            {item.displayStatus}
                                          </Typography>
                                        </Grid>
                                      )}

                                      {item?.team?.process && (
                                        <Grid item xs={6}>
                                          <Typography sx={{ fontSize: cfg.labelFontSize, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Process</Typography>
                                          <Typography sx={{ fontSize: cfg.valueFontSize, fontWeight: 600, color: '#111827' }}>
                                            {item?.team?.name || '—'}
                                          </Typography>
                                        </Grid>
                                      )}

                                      {item?.updatedAt && (
                                        <Grid item xs={6}>
                                          <Typography sx={{ fontSize: cfg.labelFontSize, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Last Updated At</Typography>
                                          <Typography sx={{ fontSize: cfg.valueFontSize, fontWeight: 600, color: '#111827', lineHeight: 1.25 }}>
                                          {new Date(item.updatedAt).toLocaleString(undefined, {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: true,
                                          })}
                                          </Typography>
                                        </Grid>
                                      )}
                                    </Grid>                        
                                  </Box>
                                  {/* Card Footer — Edit button */}
                                  <Box sx={{ position: 'absolute', bottom: '4px', right: '4px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                    <Tooltip title="Edit">
                                      <Box
                                        onClick={(e) => handleEdit(e, item)}
                                        sx={{
                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                          width: 20, height: 20, borderRadius: '5px',
                                          backgroundColor: '#F3F4F6',
                                          cursor: 'pointer',
                                          '&:hover': { backgroundColor: '#E5E7EB' },
                                          transition: 'background-color 0.15s',
                                        }}
                                      >
                                        <EditIcon sx={{ fontSize: cfg.iconSize - 1, color: '#6B7280' }} />
                                      </Box>
                                    </Tooltip>
                                  </Box>
                                </Card>
                              );
                            })
                          )}

                          {/* ── Per-column Load More ── */}
                          {(() => {
                            const loaded = col.loadedParents ?? col.items.length;
                            if (!onLoadMoreColumn || col.statusKey === undefined || loaded >= col.count) return null;
                            const isLoadingMore = loadingMoreColumns.has(columnId);
                            return (
                              <Box
                                ref={setSentinelRef(columnId)}
                                data-column-id={columnId}
                                sx={{ width: cfg.cardWidth, display: 'flex', justifyContent: 'center', py: '4px' }}
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={isLoadingMore}
                                  onClick={() => handleLoadMore(columnId)}
                                  startIcon={isLoadingMore ? <CircularProgress size={12} sx={{ color: accentColor }} /> : undefined}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.72rem',
                                    borderRadius: '8px',
                                    px: 1.5,
                                    py: '4px',
                                    color: accentColor,
                                    borderColor: PALETTE.borderAccent,
                                    backgroundColor: PALETTE.cardBg,
                                    '&:hover': { borderColor: accentColor, backgroundColor: '#F9FAFB' },
                                    '&.Mui-disabled': { color: accentColor, borderColor: PALETTE.borderAccent },
                                  }}
                                >
                                  {isLoadingMore ? 'Loading…' : `Load more (${loaded} of ${col.count})`}
                                </Button>
                              </Box>
                            );
                          })()}
                        </Box>
                      </Grid>
                    );
                  });
                })()}
              </Grid>
            ) : (
              /* Empty board state */
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', minHeight: 280, px: 3 }}>
                <Box sx={{ width: 52, height: 52, borderRadius: '14px', backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <ViewCompactIcon sx={{ color: '#D1D5DB', fontSize: 28 }} />
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#374151', mb: 0.5 }}>No transactions found</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#9CA3AF' }}>View all transactions in the Transactions page</Typography>
              </Box>
            )
          ) : null}
        </Box>

        {/* Minimap */}
        {displayColumns && Object.keys(displayColumns).length > 0 && (
          <KanbanMinimap columns={displayColumns} containerRef={scrollContainerRef} />
        )}
      </Box>

      {/* ── Delete Modal ── */}
      <WarningModal
        open={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setTransactionToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteModalOpen(false); setTransactionToDelete(null); }}
        title={`Delete Transaction: ${transactionToDelete?.key || ''}`}
        type="delete"
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleteLoading}
        message={`Are you sure you want to delete "${transactionToDelete?.key || ''}"?`}
        subHeadings={[
          'Once deleted, this transaction will be permanently removed.',
          'All associated data and history will be lost.',
          `Transaction Key: ${transactionToDelete?.key || 'N/A'}`,
        ]}
        maxWidth="sm"
        fullWidth={false}
      />

      {/* ── Edit Modal ── */}
      {editModalOpen && transactionToEdit && (
        <EditTransactionModal
          open={editModalOpen}
          completedSteps={editStepData}
          transactionItem={transactionToEdit}
          onClose={() => { setEditModalOpen(false); setTransactionToEdit(null); setEditStepsData(null); }}
          onSaveSuccess={() => { setEditModalOpen(false); setTransactionToEdit(null); setEditStepsData(null); onRefresh?.(); }}
        />
      )}

      {/* ── Acknowledge Dialog ── */}
      <Dialog
        open={confirmOpen}
        onClose={() => { setConfirmOpen(false); setPendingTx(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '14px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          },
        }}
      >
        <Box sx={{ height: 4, backgroundColor: PALETTE.success }} />

        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pt: 2.5, pb: 1, color: '#111827' }}>
          Confirm Acknowledgement
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 1.5 }}>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#6B7280', whiteSpace: 'nowrap' }}>
                {pendingTx?.team?.settings?.transactionLabel || 'Transaction'}:
              </Typography>
              <Box sx={{ border: `1px solid ${PALETTE.border}`, borderRadius: '6px', px: 1, py: '3px', fontSize: '0.82rem', fontWeight: 700, backgroundColor: '#F9FAFB', color: '#111827' }}>
                {pendingTx?.key || '—'}
              </Box>
            </Box>

            <Box sx={{ backgroundColor: pendingTx?.priorityType === 'ALERT' ? `${theme.palette.accent.dark}18` : `${theme.palette.accent.main}18`, borderRadius: '8px', p: '10px', border: `1px solid ${pendingTx?.priorityType === 'ALERT' ? `${theme.palette.accent.dark}44` : `${theme.palette.accent.main}44`}` }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: pendingTx?.priorityType === 'ALERT' ? theme.palette.accent.dark : theme.palette.accent.main, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: '6px', fontSize: '0.65rem' }}>
                {pendingTx?.priorityType === 'ALERT' ? '⚠ Alert Reasons' : '🔔 Notification Reasons'}
              </Typography>
              {pendingTx?.notificationReasons?.length ? (
                <List dense sx={{ listStyleType: 'decimal', pl: 2, '& .MuiListItem-root': { display: 'list-item', pl: 0, fontSize: '0.8rem', py: '2px', color: '#374151' } }}>
                  {pendingTx.notificationReasons.map((r: string, i: number) => (
                    <ListItem key={i}>{r}</ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.82rem' }}>
                  {pendingTx?.notificationTitle || 'No reasons provided'}
                </Typography>
              )}
            </Box>

            <Typography sx={{ fontSize: '0.78rem', color: '#6B7280' }}>
              This notification will be marked as acknowledged. Do you wish to proceed?
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0.5, gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
            onClick={async () => { setConfirmOpen(false); if (pendingTx) await handleAcknowledge(pendingTx); setPendingTx(null); }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 2.5,
              py: '7px',
              fontSize: '0.84rem',
              backgroundColor: PALETTE.success,
              boxShadow: 'none',
              '&:hover': { backgroundColor: PALETTE.successHover, boxShadow: 'none' },
            }}
          >
            Yes, Acknowledge
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => { setConfirmOpen(false); setPendingTx(null); }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 2.5,
              py: '7px',
              fontSize: '0.84rem',
              borderColor: PALETTE.border,
              color: '#6B7280',
              '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KanbanTransactions;